"use client";

import { HTMLAttributes, ReactNode } from "react";

type StickyColor =
  | "yellow"
  | "blue"
  | "green"
  | "pink"
  | "orange"
  | "purple"
  | "red"
  | "gray";

interface StickyNoteProps extends HTMLAttributes<HTMLDivElement> {
  color?: StickyColor;
  rotate?: string;
  hoverable?: boolean;
  size?: "sm" | "md" | "lg";
  children: ReactNode;
}

// CSS variable-based color system for consistency
const colorStyles: Record<
  StickyColor,
  { bg: string; shadow: string; text: string; defaultRotate: string }
> = {
  yellow: {
    bg: "var(--sticky-yellow)",
    shadow: "var(--sticky-yellow-shadow)",
    text: "var(--sticky-yellow-text)",
    defaultRotate: "-1deg",
  },
  blue: {
    bg: "var(--sticky-blue)",
    shadow: "var(--sticky-blue-shadow)",
    text: "var(--sticky-blue-text)",
    defaultRotate: "-0.5deg",
  },
  green: {
    bg: "var(--sticky-green)",
    shadow: "var(--sticky-green-shadow)",
    text: "var(--sticky-green-text)",
    defaultRotate: "0.8deg",
  },
  pink: {
    bg: "var(--sticky-pink)",
    shadow: "var(--sticky-pink-shadow)",
    text: "var(--sticky-pink-text)",
    defaultRotate: "-0.8deg",
  },
  orange: {
    bg: "var(--sticky-orange)",
    shadow: "var(--sticky-orange-shadow)",
    text: "var(--sticky-orange-text)",
    defaultRotate: "1deg",
  },
  purple: {
    bg: "var(--sticky-purple)",
    shadow: "var(--sticky-purple-shadow)",
    text: "var(--sticky-purple-text)",
    defaultRotate: "1deg",
  },
  red: {
    bg: "var(--sticky-red)",
    shadow: "var(--sticky-red-shadow)",
    text: "var(--sticky-red-text)",
    defaultRotate: "0.5deg",
  },
  gray: {
    bg: "var(--sticky-gray)",
    shadow: "var(--sticky-gray-shadow)",
    text: "var(--sticky-gray-text)",
    defaultRotate: "0.5deg",
  },
};

const sizeStyles: Record<"sm" | "md" | "lg", string> = {
  sm: "min-h-[100px] p-3",
  md: "min-h-[140px] p-4",
  lg: "min-h-[180px] p-5",
};

export function StickyNote({
  color = "yellow",
  rotate,
  hoverable = true,
  size = "md",
  className = "",
  children,
  style,
  ...props
}: StickyNoteProps) {
  const colorStyle = colorStyles[color];
  const rotation = rotate || colorStyle.defaultRotate;

  return (
    <div
      className={`
        relative rounded-sm transition-all duration-200
        ${sizeStyles[size]}
        ${hoverable ? "hover:rotate-0 hover:scale-[1.02] hover:z-10" : ""}
        ${className}
      `}
      style={{
        backgroundColor: colorStyle.bg,
        color: colorStyle.text,
        boxShadow: `0 4px 12px ${colorStyle.shadow}, var(--card-shadow-sm)`,
        transform: `rotate(${rotation})`,
        transition: "all 0.2s var(--ease-bounce)",
        ...style,
      }}
      {...props}
    >
      {/* Paper texture overlay for analog feel */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-sm"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Content */}
      <div className="relative z-[1]">{children}</div>

      {/* Bottom fold effect */}
      <div
        className="absolute bottom-0 right-0 w-6 h-6 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.06) 50%)`,
          borderRadius: "0 0 2px 0",
        }}
      />
    </div>
  );
}

export { type StickyNoteProps, type StickyColor };
